import { inject, injectable, interfaces } from 'inversify';
import { ACDLError } from '../../../../../models/error.model';
import { IMainApiService, IPagedResult } from '../../../../../services/api.service';
import { IApiService } from '../../../../../services/api.service.base';
import { IActiveFilterDto, IImageFilters } from '../imageFilters.model';
import { IProjectDetailsImage } from '../projectDetailsImages.model';

export interface IGetImagesRequest {
  projectId: string;
  pageSize: number;
  pageNumber: number;
  filters: IImageFilters;
}

interface IGetActiveFiltersRequest {
  projectId: string;
  filterId: string | undefined;
}

interface IImagesPagingResult<T> extends IPagedResult<T> {
  totalImagesCount: number;
  unlockedImagesCount: number;
  filterId: string;
}

export interface ISaveFiltersRequest {
  name: string;
  projectId: string;
  filters: IImageFilters;
}

interface IDeleteFiltersRequest {
  filterId: string;
}

export interface IUpsertFiltersRequest {
  projectId: string;
  filters: IImageFilters;
}

export interface IImageFiltersApiService {
  getProjectImagesAsync(request: IGetImagesRequest): Promise<IImagesPagingResult<IProjectDetailsImage> | ACDLError>;
  getActiveFilters(request: IGetActiveFiltersRequest): Promise<IActiveFilterDto | ACDLError>;
  saveFiltersAsync(request: ISaveFiltersRequest): Promise<void | ACDLError>;
  deleteFiltersAsync(request: IDeleteFiltersRequest): Promise<void | ACDLError>;
  upsertFiltersAsync(request: IUpsertFiltersRequest): Promise<string | ACDLError>;
}

export namespace IImageFiltersApiService {
  export const $: interfaces.ServiceIdentifier<IImageFiltersApiService> = Symbol('IMAGE_FILTERS_API_SERVICE');
}

@injectable()
export class ImageFiltersApiService implements IImageFiltersApiService {
  abortController: AbortController | undefined = undefined;

  constructor(@inject(IMainApiService.$) private readonly apiService: IApiService) {}

  async getActiveFilters(request: IGetActiveFiltersRequest): Promise<IActiveFilterDto | ACDLError> {
    return await this.apiService.getAsync<IActiveFilterDto>('/Projects/GetActiveFilters', { params: request });
  }

  async getProjectImagesAsync(request: IGetImagesRequest): Promise<IImagesPagingResult<IProjectDetailsImage> | ACDLError> {
    this.abortController?.abort();
    this.abortController = new AbortController();
    return await this.apiService.postAsync<IImagesPagingResult<IProjectDetailsImage>>('/Projects/Images', request, {
      signal: this.abortController.signal,
    });
  }

  async saveFiltersAsync(request: ISaveFiltersRequest): Promise<void | ACDLError> {
    return await this.apiService.postAsync('/Projects/SaveFilters', request);
  }

  async deleteFiltersAsync(request: IDeleteFiltersRequest): Promise<void | ACDLError> {
    return await this.apiService.postAsync('/Projects/DeleteFilters', request);
  }

  async upsertFiltersAsync(request: IUpsertFiltersRequest): Promise<string | ACDLError> {
    return await this.apiService.postAsync<string>('/Projects/UpsertImagesFilter', request);
  }
}
